/**
 * «A quién», el primer paso del asistente (SPEC-BD D8): todos los clientes (el Público de Activa), una lista guardada (un
 * público de la base), un archivo (un Excel que se cruza con el Público o vuelve como un público de la base) o los grupos
 * de una segmentación (una campaña de Activa). Lo elegido vive en el borrador (`useBorradorCampana`); acá, la opción, lo
 * que se pide para elegir y el guardado.
 *
 * - El borrador nace acá (29/10/2026): sin campaña, «Siguiente» la crea con el nombre por defecto, la audiencia y la
 *   plantilla de `?plantilla=` si vino una que sirve, y reemplaza `campanas/nueva` en el historial (nunca quedan dos
 *   borradores). Con campaña, el `PATCH {audiencia}` de siempre.
 * - Sin grupo de control: WhatsApp oficial le escribe a todos los incluidos (decisión de Alan, 23/09). Ningún guardado
 *   manda `controlPct`.
 * - NUNCA `grupos: []` desde «Elegir grupos»: para el backend `[]` es TODOS.
 * - «Por compras» y «Excluir» (28/10/2026), en el Público y la Base: se guardan con la audiencia sólo si dicen algo. Las
 *   exclusiones también con una campaña de Activa (29/10/2026); el filtro por compras no (ya sale de ellas), y se avisa
 *   antes de cambiar a esa pestaña. Con una lista de exclusión que se borró, «Siguiente» no avanza hasta sacarla.
 * - La de un seguimiento (28/10/2026) la fija la original: no se elige ni se guarda nada.
 * - Lo elegido en pantalla (`enPantalla`, 29/10/2026), para «Tu campaña»: lo que guardaría «Siguiente», en palabras, con la
 *   cuenta que muestra la pestaña (la del Público y la de la lista las pasan sus componentes; la de una segmentación es
 *   `useAlcanceOficial`). No se pide nada aparte para el panel.
 * - Lo elegido que no es lo guardado (`cambios`): saltar a otro paso sin «Siguiente» pregunta antes. «Guardar y seguir»
 *   es el «Siguiente» de acá, a ese paso; «Descartar» vuelve a la audiencia guardada.
 * - La audiencia guardada que ya no está (`guardadaYaNoEsta`, 29/10/2026): una lista de la base que se borró o una
 *   segmentación que ya no se ofrece. Se avisa arriba de su opción; antes no aparecía marcada y nada decía por qué.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AudienciaEntrada, CampanaDetalle, CampanaParaExcluir, GrupoAudiencia, ListaExclusion, ListaPublico, ModoTransporte, OpcionesCompras } from "../../types";
import { oficialApi } from "../../api";
import { enviosApi } from "../../../envios/api";
import { useEsEquipoDeenex } from "../../../campana-nueva/hooks/useEsEquipoDeenex";
import { useAlcanceOficial } from "../useAlcanceOficial";
import type { PasoAsistente } from "../../logic/chequeo";
import { ESTADOS_PLANTILLA_PARA_SEGUIR, optInConCelularDe } from "../../logic/chequeo";
import { TEXTO_SIN_BASE_DE_VENTAS, errorFiltroCompras, filtroComprasActivo, filtroParaMandar } from "../../logic/compras";
import { excluirActivo, excluirParaContar, excluirParaMandar, listasBorradas, listasSiempre } from "../../logic/exclusiones";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import { esInutilizable, hastaDonde, nombrePorDefecto, type Aviso, type CambiosDelPaso, type CtaPaso, type Pestana, type TipoPestana } from "../../logic/asistente";
import { NOMBRE_AUDIENCIA, origenDeLaSegmentacion, origenDelPublico } from "../../logic/vasAMandar";
import { detalleDelFiltroPublico, mismaAudiencia, type AudienciaEnPantalla } from "../../logic/tuCampana";
import type { ContadoPublico } from "../../components/AudienciaPublico";
import type { BorradorCampana } from "./useBorradorCampana";
import { RECHAZOS_PLANTILLA, avisoRechazoPlantilla } from "./usePlantillaCampana";

const B = "/activa/oficial";

export function useAudienciaCampana({ paso, borrador, modo, publicoUrl, analisisUrl, setErrorPaso, irA, alGuardar }: {
  paso: PasoAsistente;
  borrador: BorradorCampana;
  modo: ModoTransporte | null;
  publicoUrl: string;
  analisisUrl: string;
  setErrorPaso: (a: Aviso | null) => void;
  irA: (p: PasoAsistente, reemplazar?: boolean, sinPublico?: boolean) => void;
  /** con otra audiencia, lo calculado con la de antes (las variables, el chequeo) ya no vale */
  alGuardar: () => void;
}) {
  const navigate = useNavigate();
  const {
    campana, registrarGuardado, descartarAudiencia, guardando, setGuardando, audiencias, cargarAudiencias, publicos, cargarPublicos, plantillas, cargarPlantillas,
    plantillaId, audienciaId, modoGrupos, elegidos, publicoId, filtroPublico, filtroCompras, excluir,
  } = borrador;
  /** Pestaña elegida a mano; null = la que corresponde por lo guardado (ver `tabDerivado`). */
  const [tabElegido, setTabElegido] = useState<Pestana | null>(null);
  /** la lista recién subida en «Subir un Excel»: la pestaña del Público la muestra sin volver a pedirla */
  const [listaSubida, setListaSubida] = useState<ListaPublico | null>(null);
  /** La base de ventas en uso con la que se filtra (`GET /audiencias/compras`): null mientras llega. */
  const [opcionesCompras, setOpcionesCompras] = useState<OpcionesCompras | null>(null);
  const [errorOpcionesCompras, setErrorOpcionesCompras] = useState("");
  /** La pestaña a la que se quiso ir con algo que ahí no se usa (el filtro «Por compras» con Activa): se pregunta antes. */
  const [tabPendiente, setTabPendiente] = useState<Pestana | null>(null);
  /** Lo que se puede elegir en «Excluir» (`GET /exclusiones/campanas` y `/exclusiones/listas`): null mientras llega. */
  const [opcionesExcluir, setOpcionesExcluir] = useState<{ campanas: CampanaParaExcluir[]; listas: ListaExclusion[] } | null>(null);
  const [errorOpcionesExcluir, setErrorOpcionesExcluir] = useState("");
  /** Lo que contaron las pestañas del Público y de la lista guardada (lo mismo que muestran), para «Tu campaña». */
  const [contadoPublico, setContadoPublico] = useState<ContadoPublico | null>(null);
  const [contadoBase, setContadoBase] = useState<number | null>(null);

  const cargarOpcionesCompras = useCallback(async () => {
    setErrorOpcionesCompras("");
    try {
      const l = leerRespuesta<OpcionesCompras>(await oficialApi.opcionesCompras());
      if (!l.ok || !l.data) { setErrorOpcionesCompras(l.ok ? "El servidor no devolvió la base de ventas." : l.error || textoError(l)); return; }
      setOpcionesCompras(l.data);
    } catch (e) { setErrorOpcionesCompras(textoError(leerError(e))); }
  }, []);
  const cargarOpcionesExcluir = useCallback(async () => {
    setErrorOpcionesExcluir("");
    try {
      const [lc, ll] = await Promise.all([oficialApi.campanasParaExcluir(), oficialApi.listasExclusion()]);
      const c = leerRespuesta<CampanaParaExcluir[]>(lc);
      const l = leerRespuesta<ListaExclusion[]>(ll);
      if (!c.ok || !l.ok) { setErrorOpcionesExcluir((!c.ok ? c.error || textoError(c) : "") || (!l.ok ? l.error || textoError(l) : "")); return; }
      setOpcionesExcluir({ campanas: Array.isArray(c.data) ? c.data : [], listas: Array.isArray(l.data) ? l.data : [] });
    } catch (e) { setErrorOpcionesExcluir(textoError(leerError(e))); }
  }, []);

  // Pestaña inicial: base con un público guardado o con `?publico=`; el Público de Activa con esa audiencia
  // guardada; Activa con una audiencia de Activa guardada o con `?analisis=`; sin audiencia, el Público de Activa
  // (desde el 17/10/2026, pedido de Alan: «todo lo toma desde Público»; antes abría en la base si había públicos).
  const tabDerivado: TipoPestana | null = campana?.audiencia?.tipo === "base" || publicoUrl ? "base"
    : campana?.audiencia?.tipo === "publico" ? "publico"
    : campana?.audiencia || analisisUrl ? "activa"
    : "publico";
  const tab = tabElegido ?? tabDerivado;
  const elegirTab = (t: Pestana, confirmado = false) => {
    // El filtro «Por compras» no va con un análisis de Activa (ya sale de las compras): se avisa ANTES de cambiar, no se
    // pierde en silencio al guardar. Las exclusiones sí van con cualquier pestaña: se conservan.
    if (!confirmado && t === "activa" && tab !== "activa" && filtroComprasActivo(filtroCompras)) {
      setTabPendiente(t);
      return;
    }
    setTabPendiente(null);
    setTabElegido(t);
    setErrorPaso(null);
  };
  // El filtro «Por compras»: la base de ventas en uso se pide al llegar a la Audiencia (una vez; «Reintentar» si falló).
  useEffect(() => {
    if (paso === "audiencia" && !opcionesCompras && !errorOpcionesCompras) void cargarOpcionesCompras();
  }, [paso, opcionesCompras, errorOpcionesCompras, cargarOpcionesCompras]);
  const comprasActivo = filtroComprasActivo(filtroCompras);
  const errorCompras = comprasActivo ? errorFiltroCompras(filtroCompras) : "";
  /** el filtro con el que cuentan las pestañas: sólo uno que se puede aplicar (con base y sin errores) */
  const comprasParaContar = comprasActivo && opcionesCompras?.base && !errorCompras ? filtroCompras : null;
  /** lo que frena «Siguiente» por el filtro «Por compras»: sin base de ventas no se guarda un filtro que no se puede aplicar */
  const motivoCompras = !comprasActivo ? ""
    : !opcionesCompras ? (errorOpcionesCompras ? "No pudimos traer la base de ventas: reintentá" : "Buscando la base de ventas…")
    : !opcionesCompras.base ? TEXTO_SIN_BASE_DE_VENTAS
    : errorCompras ? "Revisá el filtro por compras" : "";
  // «Excluir»: las campañas y las listas se piden al llegar a la Audiencia (una vez; «Reintentar» o «Actualizar» las piden de nuevo).
  useEffect(() => {
    if (paso === "audiencia" && !opcionesExcluir && !errorOpcionesExcluir) void cargarOpcionesExcluir();
  }, [paso, opcionesExcluir, errorOpcionesExcluir, cargarOpcionesExcluir]);
  /** las listas elegidas que se borraron: no se cuentan, y «Siguiente» no avanza hasta sacarlas */
  const borradasExcluir = listasBorradas(excluir, campana?.audiencia?.excluir, opcionesExcluir?.listas ?? null);
  /** lo que cuentan las pestañas: lo elegido sin las listas borradas (null sin exclusiones) */
  const excluirParaContarAhora = excluirActivo(excluir) ? excluirParaContar(excluir, borradasExcluir) : null;
  const motivoExcluir = borradasExcluir.length ? "Sacá de «Excluir» la lista que se borró" : "";
  /** la marca tiene listas que valen para todas las campañas: las pestañas cuentan como la campaña aunque no elija nada */
  const hayListasSiempre = listasSiempre(opcionesExcluir?.listas).length > 0;
  /** «Reintentar» de los bloques plegados: la carga de arriba los vuelve a pedir */
  const reintentarCompras = () => { setOpcionesCompras(null); setErrorOpcionesCompras(""); };
  const reintentarExcluir = () => { setOpcionesExcluir(null); setErrorOpcionesExcluir(""); };

  const audiencia = audiencias?.find((a) => a.id === audienciaId);
  const gruposElegidos = audiencia ? (modoGrupos === "todos" ? audiencia.grupos : audiencia.grupos.filter((g) => elegidos.includes(g.id))) : [];
  /**
   * Hasta cuántos del análisis pueden recibir, con la cuenta del chequeo (`GET /audiencias/:id/alcance`): la misma que
   * mostró Reactivación en «Por dónde y cuándo». Antes el «Hasta N» salía de `optInConCelular` (un sí y un celular no
   * vacío) y no miraba los celulares sin +54, que el chequeo deja afuera: decía 148 y el chequeo daba 0.
   */
  /** sube al preparar de nuevo la campaña de Activa elegida: la cuenta se vuelve a pedir */
  const [versionAlcance, setVersionAlcance] = useState(0);
  // con las exclusiones elegidas (29/10/2026): la cuenta del análisis las descuenta, como el chequeo
  const alcanceAud = useAlcanceOficial(tab === "activa" && audienciaId ? audienciaId : null, versionAlcance, tab === "activa" ? excluirParaContarAhora : null);
  // Preparar es del equipo (la página de Mensajes y `POST …/envio`): a una persona del cliente se le dice a quién pedírselo.
  const esEquipo = useEsEquipoDeenex();
  /**
   * «Preparar de nuevo» desde la Audiencia: con el consentimiento declarado después de preparar, la campaña de Activa
   * no lo tenía (el sí se fija al preparar). Rehace el envío con los mismos grupos y mensajes; la campaña de Activa es
   * la misma, así que lo elegido sigue valiendo. Después se vuelven a contar el alcance y los grupos.
   */
  const prepararDeNuevo = async (segmentacionId: string): Promise<string | null> => {
    try {
      const l = leerRespuesta(await enviosApi.preparar(segmentacionId));
      if (!l.ok) return l.error || textoError(l);
    } catch (e) {
      return textoError(leerError(e));
    }
    setVersionAlcance((n) => n + 1);
    void cargarAudiencias();
    return null;
  };
  /** null mientras cuenta; si la cuenta no vino, la de antes (`optInConCelularDe`), que es un techo más alto */
  const puedenDelGrupo = (g: GrupoAudiencia): number | null => alcanceAud.alcance
    ? (alcanceAud.alcance.grupos.find((x) => x.id === g.id)?.pueden ?? 0)
    : alcanceAud.cargando ? null : optInConCelularDe(g);
  const puedenElegidos = gruposElegidos.reduce((s, g) => s + (puedenDelGrupo(g) ?? 0), 0);
  /** con la cuenta del chequeo en 0, la campaña no puede salir: confirmar daba «no hay a quién mandarle» */
  const audienciaSinNadie = tab === "activa" && !!alcanceAud.alcance && puedenElegidos === 0;

  /**
   * La plantilla con la que nace el borrador: la de `?plantilla=` («Usar en campaña»), si la lista dice que sirve
   * (aprobada o en revisión, no archivada, de esta cuenta). Una que no, se elige en «Qué les decís»: con ella el
   * backend no dejaría crear el borrador y el primer paso no avanzaría nunca.
   */
  const plantillaParaNacer = (): string => {
    if (!plantillaId) return "";
    if (!plantillas) return plantillaId;
    const p = plantillas.find((x) => x.id === plantillaId);
    return p && ESTADOS_PLANTILLA_PARA_SEGUIR.includes(p.estado) && !p.archivada && !esInutilizable(p, modo) ? plantillaId : "";
  };

  /** Sin campaña todavía: el borrador nace con esta audiencia, en el historial en lugar de `campanas/nueva`. */
  const crearBorrador = async (aud: AudienciaEntrada) => {
    const pid = plantillaParaNacer();
    const l = leerRespuesta<CampanaDetalle>(await oficialApi.crearCampana({ nombre: nombrePorDefecto(new Date()), audiencia: aud, ...(pid ? { plantillaId: pid } : {}) }));
    if (!l.ok) {
      setErrorPaso(RECHAZOS_PLANTILLA.has(l.code) ? avisoRechazoPlantilla(l) : { texto: l.error || textoError(l) });
      if (RECHAZOS_PLANTILLA.has(l.code)) void cargarPlantillas();
      else if (l.code === "AUDIENCIA_INVALIDA" || l.code === "GRUPO_DESCONOCIDO") void (aud.tipo === "base" ? cargarPublicos() : cargarAudiencias());
      return;
    }
    // ya quedó en el borrador: `?publico=` y `?plantilla=` no viajan más
    navigate(`${B}/campanas/${encodeURIComponent(l.data.id)}/editar?paso=mensaje`, { replace: true });
  };

  /**
   * Lo que guarda «Siguiente» con lo elegido en pantalla; null si no hay nada para guardar (un archivo sin cruzar, una
   * lista o un análisis sin elegir, ningún grupo marcado) o en un seguimiento.
   */
  const entrada = ((): AudienciaEntrada | null => {
    if (tab === null || tab === "excel" || campana?.seguimientoDe) return null;
    // Sin `controlPct` en ninguna de las tres: no hay grupo de control. El filtro «Por compras» va con el Público y la
    // Base sólo si dice algo: la audiencia se guarda entera, así que no mandarlo lo saca. Con Activa no va (se avisó
    // al cambiar de pestaña).
    const fc = filtroParaMandar(filtroCompras);
    // las exclusiones, igual: sólo si eligen algo, y con las tres (29/10/2026; antes con Activa se perdían en silencio)
    const ex = excluirParaMandar(excluir);
    const conExcluir = ex ? { excluir: ex } : {};
    const conCompras = { ...(fc ? { filtroCompras: fc } : {}), ...conExcluir };
    if (tab === "publico") return { tipo: "publico", filtroPublico, ...conCompras };
    if (tab === "base") return publicoId ? { tipo: "base", publicoId, ...conCompras } : null;
    // NUNCA `grupos: []` desde «Elegir grupos»: para el backend `[]` es TODOS (CLAUDE.md §8).
    if (!audienciaId || (modoGrupos === "elegir" && elegidos.length === 0)) return null;
    return { tipo: "activa", campanaActivaId: audienciaId, grupos: modoGrupos === "todos" ? [] : elegidos, ...conExcluir };
  })();

  /**
   * La audiencia guardada ya no se puede elegir: la lista de la base se borró, o la segmentación ya no está entre las que
   * se ofrecen. Se dice en su opción (sin frenar: el backend tiene la última palabra al guardar y en el chequeo).
   */
  const guardadaYaNoEsta = ((): { tipo: TipoPestana; texto: string } | null => {
    const g = campana?.seguimientoDe ? null : campana?.audiencia;
    if (!g) return null;
    const nombre = g.nombre ? ` («${g.nombre}»)` : "";
    if (g.tipo === "base") {
      return publicos && g.publicoId && !publicos.some((p) => p.id === g.publicoId)
        ? { tipo: "base", texto: `La lista guardada${nombre} ya no está: se borró. Elegí otra.` } : null;
    }
    if ((g.tipo || "activa") === "activa") {
      return audiencias && g.campanaActivaId && !audiencias.some((a) => a.id === g.campanaActivaId)
        ? { tipo: "activa", texto: `La segmentación guardada${nombre} ya no está para elegir: puede que se haya borrado. Elegí otra.` } : null;
    }
    return null;
  })();

  /** Lo elegido, en palabras y con la cuenta que muestra la pestaña, para «Tu campaña». */
  const enPantalla = ((): AudienciaEnPantalla | null => {
    if (!entrada) return null;
    if (entrada.tipo === "publico") {
      const f = entrada.filtroPublico;
      const detalle = detalleDelFiltroPublico(f, contadoPublico?.segmentos ?? []);
      return { entrada, texto: origenDelPublico({ archivo: f.lista ? contadoPublico?.archivo ?? "" : null, detalle }), pueden: contadoPublico?.pueden ?? null };
    }
    if (entrada.tipo === "base") {
      const p = publicos?.find((x) => x.id === entrada.publicoId);
      return { entrada, texto: p ? `${NOMBRE_AUDIENCIA.base}: ${p.nombre}` : NOMBRE_AUDIENCIA.base, pueden: contadoBase };
    }
    const nombres = gruposElegidos.filter((g) => entrada.grupos.includes(g.id)).map((g) => g.nombre);
    const pueden = alcanceAud.alcance ? (modoGrupos === "todos" ? alcanceAud.alcance.pueden : puedenElegidos) : null;
    return { entrada, texto: origenDeLaSegmentacion({ analisis: audiencia?.nombre || "", grupos: entrada.grupos, nombres }), pueden };
  })();

  /** Guarda lo elegido y sigue a `destino` («Qué les decís», con «Siguiente»), o al más lejano que deje lo guardado. */
  const guardarAudiencia = async (destino: PasoAsistente = "mensaje") => {
    if (!entrada) return;
    const base = entrada.tipo === "base";
    const delPublico = entrada.tipo === "publico";
    setGuardando(true);
    setErrorPaso(null);
    try {
      if (!campana) { await crearBorrador(entrada); return; }
      const l = leerRespuesta<CampanaDetalle>(await oficialApi.editarCampana(campana.id, { audiencia: entrada }));
      if (!l.ok) {
        setErrorPaso({ texto: l.error || textoError(l) });
        if (!delPublico && (l.code === "AUDIENCIA_INVALIDA" || l.code === "GRUPO_DESCONOCIDO")) void (base ? cargarPublicos() : cargarAudiencias());
        return;
      }
      const n = registrarGuardado(l.data);
      setTabElegido(null);
      // Con otra audiencia el chequeo y la previa de las variables ya no valen: se recalculan al entrar.
      alGuardar();
      irA(hastaDonde(destino, n), false, true);
    } catch (e) {
      setErrorPaso({ texto: textoError(leerError(e)) });
    } finally {
      setGuardando(false);
    }
  };

  // El Público de Activa siempre se puede guardar (sin filtro = todos): si hoy nadie puede recibir, la pestaña lo
  // dice y por qué; la campaña se puede armar y probar mientras se cargan los consentimientos en la Base.
  const motivo = tab === null ? "Cargando los públicos…"
    : tab === "publico" ? motivoCompras || motivoExcluir
    : tab === "base" ? (!publicoId ? "Elegí un público" : motivoCompras || motivoExcluir)
    : tab === "excel" ? "Subí el archivo y elegí a quiénes mandarle"
    : !audienciaId ? "Elegí un análisis de ventas"
    : modoGrupos === "elegir" && elegidos.length === 0 ? "Marcá al menos un grupo"
    : motivoExcluir ? motivoExcluir
    : audienciaSinNadie ? "Nadie de esa audiencia puede recibir por WhatsApp oficial" : "";
  // La de un seguimiento la fija la original: no hay nada que guardar acá.
  const cta: CtaPaso = campana?.seguimientoDe ? { motivo: null, onSiguiente: async () => irA("mensaje") }
    : { motivo: motivo || null, onSiguiente: () => guardarAudiencia(), guardando };
  /** Lo elegido no es lo guardado (en un seguimiento no se elige nada). Al irse, `?publico=` ya no viaja. */
  const cambios: CambiosDelPaso = {
    sinGuardar: !!campana && !campana.seguimientoDe && !!entrada && !mismaAudiencia(entrada, campana.audiencia),
    noSeGuarda: motivo || null,
    guardar: guardarAudiencia,
    descartar: (destino) => { setTabElegido(null); setTabPendiente(null); descartarAudiencia(); irA(destino, false, true); },
  };

  return {
    cta, cambios, guardadaYaNoEsta, enPantalla, setContadoPublico, setContadoBase, tab, elegirTab, tabPendiente, setTabPendiente, listaSubida, setListaSubida,
    opcionesCompras, errorOpcionesCompras, reintentarCompras, comprasParaContar,
    opcionesExcluir, errorOpcionesExcluir, reintentarExcluir, excluirParaContarAhora, hayListasSiempre,
    alcanceAud, puedenDelGrupo, puedenElegidos, prepararDeNuevo: esEquipo ? prepararDeNuevo : undefined,
  };
}

export type AudienciaCampana = ReturnType<typeof useAudienciaCampana>;
